import React from 'react';

interface LocationMapProps {
    project: any;
}

const LocationMap: React.FC<LocationMapProps> = ({ project }) => {
    // Priority: 1. googleMapLocation URL from DB, 2. Search query URL using latitude and longitude, 3. Address and City search
    const getGoogleMapUrl = () => {
        if (project.mapDetails?.googleMapLocation) {
            return project.mapDetails.googleMapLocation;
        }
        if (project.mapDetails?.latitude && project.mapDetails?.longitude) {
            return `https://www.google.com/maps/search/?api=1&query=${project.mapDetails.latitude},${project.mapDetails.longitude}`;
        }
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(project.address || project.name || '')}+${encodeURIComponent(project.city || '')}`;
    };

    const mapUrl = getGoogleMapUrl();

    // Generate the embedded iframe map source with Satellite view (t=k) and close-up zoom (z=18)
    const getEmbedMapUrl = () => {
        if (project.mapDetails?.latitude && project.mapDetails?.longitude) {
            return `https://maps.google.com/maps?q=${project.mapDetails.latitude},${project.mapDetails.longitude}&t=k&z=18&output=embed`;
        }
        return `https://maps.google.com/maps?q=${encodeURIComponent(project.address || '')}+${encodeURIComponent(project.city || '')}&t=k&z=18&output=embed`;
    };

    const embedUrl = getEmbedMapUrl();

    return (
        <div id="project-location" className="mb-4 scroll-mt-32">
            <div className="bg-white rounded-xl overflow-hidden shadow-xl shadow-slate-200/50 border border-slate-100">
                {/* Header */}
                <div className="p-8 pb-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-xl md:text-2xl font-bold text-black leading-tight">Project Location Map</h3>
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                            <span className="text-[12px] font-medium text-black">Live View</span>
                        </div>
                    </div>
                </div>

                {/* Map View & Floating Overlay */}
                <div className="relative w-full h-[450px]">
                    {/* Embedded Interactive Google Map */}
                    <iframe 
                        src={embedUrl} 
                        width="100%" 
                        height="450" 
                        style={{ border: '0' }} 
                        allowFullScreen={true}
                        loading="lazy" 
                        referrerPolicy="no-referrer-when-downgrade"
                        title={`${project.name} Location Map`}
                        className="w-full h-full"
                    ></iframe>

                    {/* Floating Info Overlay Card - Bottom Left */}
                    <div className="absolute bottom-6 left-6 right-6 pointer-events-none z-10">
                        <div className="bg-[#001b4a]/90 backdrop-blur-md text-white p-6 rounded-2xl shadow-2xl border border-white/10 max-w-sm pointer-events-auto transform transition-all duration-300 hover:scale-[1.02]">
                            <span className="text-[9px] font-bold tracking-widest text-[#f1ae24] uppercase mb-1 block">PROJECT LOCATION</span>
                            <h4 className="font-bold text-white text-[18px] mb-1 truncate max-w-[280px]">{project.name}</h4>
                            <p className="text-white/80 text-[12px] font-medium mb-4 truncate max-w-[280px]">{project.location}, {project.city}</p>
                            
                            <a 
                                href={mapUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 bg-[#f1ae24] hover:bg-[#f9c253] text-[#001b4a] px-5 py-2.5 rounded-xl text-[11px] font-extrabold transition-all shadow-lg shadow-[#f1ae24]/20 hover:scale-105 active:scale-95"
                            >
                                <i className="fa-solid fa-diamond-turn-right text-xs"></i>
                                Open in Google Maps
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LocationMap;
